import { World } from '@ecs/World';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Renderable } from '@components/Renderable';
import { Health } from '@components/Health';
import { HealthDisplay } from '@components/HealthDisplay';
import { Tag } from '@components/Tag';
import { ShipClass } from '@components/ShipClass';
import { RosterLink } from '@components/RosterLink';
import { Selectable } from '@components/Selectable';
import { TargetHighlight } from '@components/TargetHighlight';
import { Docked } from '@components/Docked';
import { Mothership } from '@components/Mothership';
import { Assembling } from '@components/Assembling';
import { Welding } from '@components/Welding';
import { HangarEffectKind, HangarEffectRequest } from '@components/HangarEffectRequest';
import { EntityFactory } from '@entities/EntityFactory';
import { PLAYER_SHIP_INFO, PlayerShipClass } from '@entities/PlayerShips';
import { BuildResult, DeployResult, Fleet, RosterShip } from '@/fleet/Fleet';
import { LevelConfig, LEVELS } from '@/levels/LevelData';
import { BaseLayout, berthPosition } from '@/base/BaseLayout';
import { tintColor } from '@utils/ColorUtils';

/** The slice of ProgressManager the hangar needs. */
export interface HangarProgress {
  getFleet(): Fleet;
  saveProgress(): void;
  getHighestUnlockedLevel(): number;
}

const MOTHERSHIP_COLOR = '#46566f';
const LAUNCH_STAGGER = 14; // frames between ships leaving

/**
 * The mothership's actions: build, repair, deploy and launch. Each one updates the
 * roster, saves, and asks for its visual effect and sound with a HangarEffectRequest.
 */
export class HangarController {
  private ships = new Map<number, Entity>();
  private selectedId: number | null = null;
  private launching: number[] = [];

  constructor(
    private readonly world: World,
    private readonly progress: HangarProgress,
    private readonly layout: () => BaseLayout
  ) {}

  private get fleet(): Fleet {
    return this.progress.getFleet();
  }

  /** Rebuilds the mothership and a docked entity for every roster ship. */
  populate(): void {
    this.world.clear();
    this.ships.clear();
    this.launching = [];

    const layout = this.layout();
    this.world.addEntity(new Entity()
      .addComponent(new Position(layout.center.x, layout.center.y))
      .addComponent(new Velocity(layout.angle, 0, 1, 0))
      .addComponent(new Renderable('mothership', MOTHERSHIP_COLOR, layout.size, tintColor(MOTHERSHIP_COLOR, 0.35), 1.5, '#2d5a90', 16))
      .addComponent(new Mothership()));

    this.fleet.trimDeployment(this.getDeploymentLimit());
    for (const ship of this.fleet.getShips()) this.dock(ship);

    if (this.selectedId !== null && !this.ships.has(this.selectedId)) this.selectedId = null;
    this.select(this.selectedId);
  }

  getShipEntity(id: number): Entity | undefined {
    return this.ships.get(id);
  }

  getShipEntities(): Entity[] {
    return [...this.ships.values()];
  }

  getSelectedId(): number | null {
    return this.selectedId;
  }

  getSelectedShip(): RosterShip | undefined {
    return this.selectedId === null ? undefined : this.fleet.getShip(this.selectedId);
  }

  select(id: number | null): void {
    this.selectedId = id !== null && this.ships.has(id) ? id : null;
    for (const [shipId, entity] of this.ships) {
      entity.getComponent(Selectable)!.selected = shipId === this.selectedId;
    }
  }

  /** The mission the base is preparing for: the furthest unlocked level. */
  getTargetLevel(): LevelConfig {
    const level = this.progress.getHighestUnlockedLevel();
    return LEVELS.find(l => l.id === level) ?? LEVELS[0];
  }

  getDeploymentLimit(): number {
    return this.getTargetLevel().deploymentLimit;
  }

  build(shipClass: PlayerShipClass): BuildResult {
    const result = this.fleet.build(shipClass, this.progress.getHighestUnlockedLevel());
    if (!result.ok) {
      this.requestAtMothership('deny');
      return result;
    }

    // New ships go straight into the deployment when there is room
    this.fleet.toggleDeploy(result.ship.id, this.getDeploymentLimit());
    this.progress.saveProgress();

    const entity = this.dock(result.ship);
    entity.addComponent(new Assembling());
    entity.getComponent(Renderable)!.alpha = 0;
    this.requestAt(entity, 'build', PLAYER_SHIP_INFO[shipClass].color);
    this.select(result.ship.id);
    return result;
  }

  repair(id: number): boolean {
    const entity = this.ships.get(id);
    if (!entity || !this.fleet.repair(id)) {
      entity ? this.requestAt(entity, 'deny') : this.requestAtMothership('deny');
      return false;
    }

    this.progress.saveProgress();
    entity.getComponent(Health)!.current = this.fleet.getShip(id)!.hull;
    entity.addComponent(new Welding());
    this.requestAt(entity, 'repair', '#ffc266');
    return true;
  }

  toggleDeploy(id: number): DeployResult {
    const entity = this.ships.get(id);
    const result = this.fleet.toggleDeploy(id, this.getDeploymentLimit());
    if (!entity || result === 'missing' || result === 'full') {
      entity ? this.requestAt(entity, 'deny') : this.requestAtMothership('deny');
      return result;
    }

    this.progress.saveProgress();
    entity.getComponent(TargetHighlight)!.isTargeted = result === 'deployed';
    this.requestAt(entity, result === 'deployed' ? 'deploy' : 'recall');
    return result;
  }

  /** Starts the launch sequence for the deployed ships. */
  launch(): boolean {
    const deployed = this.fleet.getDeployed().filter(ship => this.ships.has(ship.id));
    if (deployed.length === 0 || this.isLaunching()) {
      this.requestAtMothership('deny');
      return false;
    }

    this.launching = deployed.map(ship => ship.id);
    deployed.forEach((ship, i) => {
      const docked = this.ships.get(ship.id)!.getComponent(Docked)!;
      docked.phase = 'undocking';
      docked.frame = 0;
      docked.delay = i * LAUNCH_STAGGER;
    });
    this.select(null);
    this.requestAtMothership('launch', '#7fb8ff');
    return true;
  }

  isLaunching(): boolean {
    return this.launching.length > 0 && !this.isLaunchComplete();
  }

  isLaunchComplete(): boolean {
    return this.launching.length > 0 &&
      this.launching.every(id => this.ships.get(id)?.getComponent(Docked)?.phase === 'gone');
  }

  /** Queues a free-standing effect, e.g. credits arriving at a screen position. */
  requestEffect(kind: HangarEffectKind, x: number, y: number, color?: string): void {
    this.world.addEntity(new Entity().addComponent(new HangarEffectRequest(kind, x, y, color)));
  }

  private dock(ship: RosterShip): Entity {
    const layout = this.layout();
    const at = berthPosition(layout, ship.berth);
    const combat = EntityFactory.createPlayerShip(ship.shipClass, at.x, at.y);
    const renderable = combat.getComponent(Renderable)!;
    renderable.size *= layout.shipScale;

    const entity = new Entity()
      .addComponent(new Position(at.x, at.y))
      .addComponent(new Velocity(layout.angle, 0, combat.getComponent(Velocity)!.maxSpeed, 0))
      .addComponent(renderable)
      .addComponent(new Health(ship.hull, ship.maxHull))
      .addComponent(new HealthDisplay(true, ship.hull / ship.maxHull, '#00ff00', renderable.size * 2.2, 4, renderable.size * 1.7))
      .addComponent(new Tag('player'))
      .addComponent(new ShipClass(ship.shipClass))
      .addComponent(new RosterLink(ship.id, ship.name))
      .addComponent(new Selectable(ship.id === this.selectedId))
      .addComponent(new TargetHighlight(this.fleet.isDeployed(ship.id), '#50ffa0', renderable.size * 0.7))
      .addComponent(new Docked(ship.berth, (ship.id * 1.7) % 6));

    this.world.addEntity(entity);
    this.ships.set(ship.id, entity);
    return entity;
  }

  private requestAt(entity: Entity, kind: HangarEffectKind, color?: string): void {
    const position = entity.getComponent(Position)!;
    const renderable = entity.getComponent(Renderable)!;
    this.world.addEntity(new Entity().addComponent(
      new HangarEffectRequest(kind, position.x, position.y, color ?? renderable.color, renderable.size)
    ));
  }

  private requestAtMothership(kind: HangarEffectKind, color?: string): void {
    const layout = this.layout();
    this.world.addEntity(new Entity().addComponent(
      new HangarEffectRequest(kind, layout.center.x, layout.center.y, color, layout.size * 0.2)
    ));
  }
}
